import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAllComparisonItems } from '../services/dataService';
import { ComparisonItem } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import ShareButton from '../components/ShareButton';
import { useCountry } from '../context/CountryContext';

// Helper function to parse various amount strings into a comparable number
const parseAmount = (amount: string): number => {
    // Handle "Free", "$0", "₹0" cases
    if (amount.toLowerCase() === 'free' || amount.startsWith('$0') || amount.startsWith('₹0')) {
        return 0;
    }
    // Extract the first number found in the string
    const match = amount.match(/(\d+(\.\d+)?)/);
    if (match) {
        return parseFloat(match[0]);
    }
    // Return a high number if no numeric value can be found
    return Infinity;
};

const ComparisonCard: React.FC<{
    item: ComparisonItem;
    onRemove: (id: string) => void;
    isLowest?: boolean;
}> = ({ item, onRemove, isLowest }) => (
    <div className={`relative bg-white rounded-lg shadow-md p-6 flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
        isLowest ? 'border-2 border-primary' : 'border border-gray-200'
    }`}>
        {isLowest && (
            <div className="absolute top-0 right-0 -mt-3 -mr-3 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg z-10">
                Lowest Fee
            </div>
        )}
        <div className="flex-grow">
            <h3 className="text-lg font-bold font-poppins text-secondary">{item.sourceName}</h3>
            {/* The item type is now a group header, so it can be omitted from the card for less redundancy */}
            {/* <p className="text-gray-600 mt-1">{item.type}</p> */}
            <p className={`text-2xl font-bold my-3 font-roboto-mono ${isLowest ? 'text-primary' : 'text-primary-dark'}`}>{item.amount}</p>
            <p className="text-sm text-neutral-light">{item.description}</p>
        </div>
        <button
            onClick={() => onRemove(item.id)}
            className="mt-4 text-sm font-medium text-[#FF5F57] hover:text-red-700 self-start"
        >
            Remove
        </button>
    </div>
);


const ComparisonPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { country } = useCountry();
    const [allItems, setAllItems] = useState<ComparisonItem[]>([]);
    const [selectedItems, setSelectedItems] = useState<ComparisonItem[]>([]);
    const [loading, setLoading] = useState(true);

    const updateUrlAndStorage = (currentItems: ComparisonItem[]) => {
        const itemIds = currentItems.map(item => item.id);
        if (itemIds.length > 0) {
            setSearchParams({ items: itemIds.join(',') });
            localStorage.setItem('comparisonItems', itemIds.join(','));
        } else {
            setSearchParams({});
            localStorage.removeItem('comparisonItems');
        }
    };

    useEffect(() => {
        setLoading(true);
        getAllComparisonItems(country).then(data => {
            setAllItems(data);
            
            const itemIdsFromUrl = searchParams.get('items')?.split(',').filter(Boolean) || [];
            const itemIdsFromStorage = localStorage.getItem('comparisonItems')?.split(',').filter(Boolean) || [];
            const mergedIds = Array.from(new Set([...itemIdsFromUrl, ...itemIdsFromStorage]));
            const initialItems = data.filter(item => mergedIds.includes(item.id));
            
            setSelectedItems(initialItems);
            updateUrlAndStorage(initialItems);

            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country, searchParams.toString()]);

    const handleAddItem = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedId = event.target.value;
        if (!selectedId || selectedItems.some(item => item.id === selectedId)) {
            return;
        }
        const itemToAdd = allItems.find(item => item.id === selectedId);
        if (itemToAdd) {
            const newSelectedItems = [...selectedItems, itemToAdd];
            setSelectedItems(newSelectedItems);
            updateUrlAndStorage(newSelectedItems);
        }
    };

    const handleRemoveItem = (idToRemove: string) => {
        const newSelectedItems = selectedItems.filter(item => item.id !== idToRemove);
        setSelectedItems(newSelectedItems);
        updateUrlAndStorage(newSelectedItems);
    };
    
    // New logic to group items and find the lowest fee in each group
    const groupedAndHighlightedItems = useMemo(() => {
        if (selectedItems.length === 0) return [];
        
        // 1. Group items by type
        const grouped: Record<string, ComparisonItem[]> = selectedItems.reduce((acc, item) => {
            (acc[item.type] = acc[item.type] || []).push(item);
            return acc;
        }, {} as Record<string, ComparisonItem[]>);

        // 2. Find the lowest fee item in each group
        const lowestFeeItemIds = new Set<string>();
        Object.values(grouped).forEach(group => {
            if (group.length > 1) { // Only highlight if there's something to compare
                const lowestItem = group.reduce((lowest, current) => {
                    return parseAmount(current.amount) < parseAmount(lowest.amount) ? current : lowest;
                });
                lowestFeeItemIds.add(lowestItem.id);
            }
        });

        // 3. Return a structure that's easy to render
        return Object.entries(grouped).map(([type, items]) => ({
            type,
            items,
            lowestFeeItemIds,
        }));
    }, [selectedItems]);


    return (
        <div className="bg-background py-12">
            <div className="container mx-auto px-6">
                <header className="text-center mb-10 relative">
                     <h1 className="text-4xl font-extrabold font-poppins tracking-tight text-secondary sm:text-5xl">Compare Charges Side-by-Side</h1>
                     <p className="mt-4 text-xl text-neutral-light">Select charges to see which option is best for you.</p>
                </header>
                
                {loading ? <LoadingSpinner /> : (
                    <>
                        <section className="max-w-2xl mx-auto bg-white p-6 rounded-lg shadow-md mb-10 border border-gray-200">
                            <label htmlFor="charge-select" className="block text-sm font-medium text-gray-700 mb-2">Add a charge to compare:</label>
                            <select
                                id="charge-select"
                                onChange={handleAddItem}
                                value=""
                                className="block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary bg-gray-50"
                            >
                                <option value="" disabled>-- Select an option --</option>
                                {allItems
                                    .sort((a, b) => a.sourceName.localeCompare(b.sourceName))
                                    .map(item => (
                                    <option key={item.id} value={item.id} disabled={selectedItems.some(si => si.id === item.id)}>
                                        {item.sourceName} - {item.type} ({item.amount})
                                    </option>
                                ))}
                            </select>
                        </section>

                        {selectedItems.length > 0 && (
                             <div className="text-center mb-10">
                                <ShareButton
                                    title="Check out this charge comparison"
                                    text="I've created a side-by-side comparison of different financial charges on ChargesDecoder. See the results here!"
                                />
                             </div>
                        )}

                        <section>
                            {selectedItems.length > 0 ? (
                                <div className="space-y-10">
                                    {groupedAndHighlightedItems.map(group => (
                                        <div key={group.type}>
                                            <h2 className="text-2xl font-bold font-poppins text-secondary mb-4 border-b pb-2">{group.type}</h2>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                {group.items.map(item => (
                                                    <ComparisonCard
                                                        key={item.id}
                                                        item={item}
                                                        onRemove={handleRemoveItem}
                                                        isLowest={group.lowestFeeItemIds.has(item.id)}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-10 px-6 bg-white rounded-lg shadow-sm border border-gray-200">
                                    <h2 className="text-xl font-medium text-secondary">Your comparison list is empty</h2>
                                    <p className="text-neutral-light mt-2">Use the dropdown above or the 'Add to Compare' buttons on charge detail pages.</p>
                                </div>
                            )}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

export default ComparisonPage;